<template>
  <tr>
    <td colspan="6" class="p-0">
      <div
        class="overflow-hidden transition-all duration-300 ease-out"
        :style="{ maxHeight: isExpanded ? '600px' : '0px' }"
      >
        <div class="bg-gray-50 border-t border-gray-100 px-6 py-5">
          <!-- Full-width Tabs -->
          <div class="flex bg-white rounded-lg p-1 border border-gray-200 mb-5">
            <button
              v-for="stage in stages"
              :key="stage"
              @click="activeTab = stage"
              class="flex-1 px-4 py-2.5 text-sm font-medium rounded-md transition-all duration-200"
              :class="
                activeTab === stage
                  ? 'bg-cosmicPurple/20 text-cosmicPurple shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              "
            >
              <span class="flex items-center justify-center gap-2">
                <component :is="getStageIconComponent(stage)" class="w-4 h-4" />
                {{ getStageTitle(stage) }}
                <!-- Status indicator dot -->
                <span
                  class="w-2 h-2 rounded-full flex-shrink-0"
                  :class="getStatusDotClass(adventurer[`${stage}_status`])"
                ></span>
              </span>
            </button>
          </div>

          <!-- Tab Content - Inline Editable -->
          <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div class="p-5">
              <!-- Two column layout: Left for Status & Attempts, Right for Notes -->
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <!-- Left Column: Status & Attempts -->
                <div class="space-y-5">
                  <!-- Status Dropdown -->
                  <div>
                    <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                      Status
                    </label>
                    <CodingStatusSelect
                      v-if="activeTab === 'coding'"
                      :model-value="localData[activeTab].status"
                      @update:model-value="(val) => updateLocalData('status', val)"
                      :disabled="isUpdatingStatus[`${adventurer.id}_${activeTab}`]"
                    />
                    <SystemDesignStatusSelect
                      v-else-if="activeTab === 'system_design'"
                      :model-value="localData[activeTab].status"
                      @update:model-value="(val) => updateLocalData('status', val)"
                      :disabled="isUpdatingStatus[`${adventurer.id}_${activeTab}`]"
                    />
                    <StageStatusSelect
                      v-else
                      :model-value="localData[activeTab].status"
                      @update:model-value="(val) => updateLocalData('status', val)"
                      :disabled="isUpdatingStatus[`${adventurer.id}_${activeTab}`]"
                    />
                  </div>

                  <!-- Attempts Counter -->
                  <div>
                    <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                      Attempts
                    </label>
                    <div class="flex items-center gap-3">
                      <button
                        @click="updateLocalData('attempts', Math.max(0, localData[activeTab].attempts - 1))"
                        class="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 disabled:opacity-50 transition-colors"
                        :disabled="localData[activeTab].attempts <= 0 || isUpdatingStatus[`${adventurer.id}_${activeTab}`]"
                      >
                        <Minus class="w-4 h-4" />
                      </button>
                      <div class="flex items-center justify-center w-14 h-10 bg-gray-50 border border-gray-200 rounded-lg">
                        <span class="text-lg font-semibold text-gray-900">
                          {{ localData[activeTab].attempts }}
                        </span>
                      </div>
                      <button
                        @click="updateLocalData('attempts', localData[activeTab].attempts + 1)"
                        class="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 disabled:opacity-50 transition-colors"
                        :disabled="isUpdatingStatus[`${adventurer.id}_${activeTab}`]"
                      >
                        <Plus class="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Right Column: Notes -->
                <div class="flex flex-col">
                  <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Notes
                  </label>
                  <textarea
                    :value="localData[activeTab].notes"
                    @input="(e) => updateLocalData('notes', (e.target as HTMLTextAreaElement).value)"
                    class="flex-1 min-h-[120px] px-4 py-3 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent focus:bg-white transition-all resize-none"
                    :placeholder="`Add notes about ${getStageTitle(activeTab).toLowerCase()} progress...`"
                    :disabled="isUpdatingStatus[`${adventurer.id}_${activeTab}`]"
                  />
                </div>
              </div>

              <!-- Save Button - Only show when there are changes -->
              <div 
                v-if="hasChanges"
                class="flex justify-end gap-3 mt-5 pt-5 border-t border-gray-100"
              >
                <button
                  @click="resetChanges"
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all"
                  :disabled="isUpdatingStatus[`${adventurer.id}_${activeTab}`]"
                >
                  Cancel
                </button>
                <button
                  @click="saveChanges"
                  class="px-4 py-2 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all disabled:opacity-50 flex items-center gap-2"
                  :disabled="isUpdatingStatus[`${adventurer.id}_${activeTab}`]"
                >
                  <Loader2 v-if="isUpdatingStatus[`${adventurer.id}_${activeTab}`]" class="animate-spin w-4 h-4" />
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </td>
  </tr>
</template>

<script setup lang="ts">
import { ref, watch, computed, reactive } from 'vue'
import { Plus, Minus, Loader2, FileBadge, Speech, SquareCode, Cog } from 'lucide-vue-next'
import type { Adventurer, Stage } from '@/types/adventurer'
import { getStageTitle, getStageIcon } from '@/const/progressStatus'
import StageStatusSelect from './StageStatusSelect.vue'
import CodingStatusSelect from './CodingStatusSelect.vue'
import SystemDesignStatusSelect from './SystemDesignStatusSelect.vue'

const props = defineProps<{
  adventurer: Adventurer
  isExpanded: boolean
  isUpdatingStatus: Record<string, boolean>
  /** Incremented by the parent when a resume upload modal is cancelled, triggering a local reset. */
  resumeResetTrigger?: number
}>()

const emit = defineEmits<{
  (e: 'stage-update', stage: Stage, changes: { status?: string; attempts?: number; notes?: string }): void
}>()

const stages: Stage[] = ['resume', 'behavioral', 'coding', 'system_design']
const activeTab = ref<Stage>('resume')

// Local editable data for each stage
const localData = reactive<Record<Stage, { status: string; attempts: number; notes: string }>>({
  resume: { status: '', attempts: 0, notes: '' },
  behavioral: { status: '', attempts: 0, notes: '' },
  coding: { status: '', attempts: 0, notes: '' },
  system_design: { status: '', attempts: 0, notes: '' }
})

// Icon components mapping
const iconComponents = {
  FileBadge,
  Speech,
  SquareCode,
  Cog
}

// Initialize local data from adventurer
const initializeLocalData = () => {
  stages.forEach(stage => {
    localData[stage] = {
      status: props.adventurer[`${stage}_status`] || '',
      attempts: props.adventurer[`${stage}_status_attempts`] || 0,
      notes: props.adventurer[`${stage}_status_notes`] || ''
    }
  })
}

// Watch for adventurer changes
watch(() => props.adventurer, () => {
  initializeLocalData()
}, { immediate: true, deep: true })

// When the parent cancels a resume upload modal, revert just the resume status locally
watch(() => props.resumeResetTrigger, () => {
  localData.resume.status = props.adventurer['resume_status'] || ''
})

// Check if current tab has changes
const hasChanges = computed(() => {
  const stage = activeTab.value
  const original = {
    status: props.adventurer[`${stage}_status`] || '',
    attempts: props.adventurer[`${stage}_status_attempts`] || 0,
    notes: props.adventurer[`${stage}_status_notes`] || ''
  }
  return (
    localData[stage].status !== original.status ||
    localData[stage].attempts !== original.attempts ||
    localData[stage].notes !== original.notes
  )
})

const getStageIconComponent = (stage: Stage) => {
  const iconName = getStageIcon(stage)
  return iconComponents[iconName as keyof typeof iconComponents] || FileBadge
}

const getStatusDotClass = (status: string | undefined) => {
  const s = status?.toLowerCase() || ''
  if (s.includes('passed') || s.includes('completed')) return 'bg-emerald-500'
  if (s.includes('failed') || s.includes('didnt_pass')) return 'bg-red-500'
  if (s.includes('in_progress') || s.includes('scheduled')) return 'bg-blue-500'
  return 'bg-gray-300'
}

const updateLocalData = (field: 'status' | 'attempts' | 'notes', value: string | number) => {
  const stage = activeTab.value
  if (field === 'attempts') {
    localData[stage].attempts = value as number
  } else {
    localData[stage][field] = value as string
  }
}

const resetChanges = () => {
  const stage = activeTab.value
  localData[stage] = {
    status: props.adventurer[`${stage}_status`] || '',
    attempts: props.adventurer[`${stage}_status_attempts`] || 0,
    notes: props.adventurer[`${stage}_status_notes`] || ''
  }
}

const saveChanges = () => {
  const stage = activeTab.value
  const original = {
    status: props.adventurer[`${stage}_status`] || '',
    attempts: props.adventurer[`${stage}_status_attempts`] || 0,
    notes: props.adventurer[`${stage}_status_notes`] || ''
  }

  const changes: { status?: string; attempts?: number; notes?: string } = {}

  if (localData[stage].status !== original.status) {
    changes.status = localData[stage].status
  }
  if (localData[stage].attempts !== original.attempts) {
    changes.attempts = localData[stage].attempts
  }
  if (localData[stage].notes !== original.notes) {
    changes.notes = localData[stage].notes
  }

  if (Object.keys(changes).length > 0) {
    emit('stage-update', stage, changes)
  }
}
</script>
